import { TFunction } from 'i18next';

import { deriveTicketDescriptionKey } from '../enrichment';
import { ITicketCard } from '../types';

import { formatEuroFromCents } from './formatPrice';

/**
 * Build the subtitle text shown on a ticket card.
 *
 * Rules (matches Figma node 14908:74334 / 15500:84228):
 * - Subscription tickets render the recurring monthly debit ("Ab X,XX €/Monat").
 *   Per Ticketshop API §9, startingPriceInCents is already the per-period
 *   (monthly) price — display it directly, do NOT divide by periodCount.
 * - All other tickets render the listed price ("Ab X,XX €").
 * - When a description ("1 Tag", "12 Monate", "Bis zu 3 Haltestellen", …) can
 *   be derived from category + name, it is prepended: "{description} – {price}".
 */
export const buildTicketSubtitle = (
	ticket: ITicketCard,
	t: TFunction<'ticketshop'>
): string => {
	const isSubscription = ticket.category === 'subscription';

	const priceKey = isSubscription
		? 'operator_tickets.starting_price_per_month'
		: 'operator_tickets.starting_price';

	const price = t(priceKey, {
		price: formatEuroFromCents(ticket.startingPriceInCents),
	});

	const descriptionKey = deriveTicketDescriptionKey(
		ticket.category,
		ticket.name
	);

	if (!descriptionKey) {
		return price;
	}

	return t('operator_tickets.subtitle_with_description', {
		description: t(descriptionKey),
		price,
	});
};
